import { useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { AlertCircle, TrendingUp, CheckCircle2, Clock } from "lucide-react"

interface DashboardStats {
  totalReports: number
  verifiedToday: number
  pendingVerification: number
  broadcastSent: number
  dailyData: Array<{ date: string; reports: number; verified: number }>
  platformBreakdown: Array<{ name: string; value: number }>
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null)

  useEffect(() => {
    const mockStats: DashboardStats = {
      totalReports: 1247,
      verifiedToday: 45,
      pendingVerification: 128,
      broadcastSent: 892,
      dailyData: [
        { date: "Mon", reports: 120, verified: 85 },
        { date: "Tue", reports: 145, verified: 92 },
        { date: "Wed", reports: 98, verified: 68 },
        { date: "Thu", reports: 167, verified: 110 },
        { date: "Fri", reports: 189, verified: 125 },
        { date: "Sat", reports: 156, verified: 98 },
        { date: "Sun", reports: 142, verified: 95 },
      ],
      platformBreakdown: [
        { name: "Website", value: 320 },
        { name: "Facebook", value: 285 },
        { name: "Instagram", value: 245 },
        { name: "Twitter", value: 198 },
        { name: "TikTok", value: 156 },
        { name: "YouTube", value: 43 },
      ],
    }
    setStats(mockStats)
  }, [])

  if (!stats) return <div>Loading...</div>

  const COLORS = [
    "#3b82f6", // Blue
    "#10b981", // Green
    "#f59e0b", // Amber
    "#8b5cf6", // Purple
    "#ef4444", // Red
    "#06b6d4", // Cyan
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Dashboard Overview</h1>
        <p className="text-muted-foreground mt-2">Monitoring pemberantasan akun promotor judi online</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalReports}</div>
            <p className="text-xs text-muted-foreground">All time reports</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Verified Today</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.verifiedToday}</div>
            <p className="text-xs text-muted-foreground">Verified accounts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingVerification}</div>
            <p className="text-xs text-muted-foreground">Awaiting verification</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Broadcast Sent</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.broadcastSent}</div>
            <p className="text-xs text-muted-foreground">Reports sent to platforms</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts - Reduced height and improved layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Weekly Activity</CardTitle>
            <CardDescription>Reports and verifications per day</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                reports: { label: "Reports", color: "#3b82f6" },
                verified: { label: "Verified", color: "#10b981" },
              }}
              className="h-[250px] w-full"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.dailyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="reports" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="verified" fill="#10b981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Platform Breakdown</CardTitle>
            <CardDescription>Reports by platform</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <ChartContainer
              config={{
                value: { label: "Reports", color: "#3b82f6" },
              }}
              className="h-[250px] w-full max-w-sm"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.platformBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.platformBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Statistics</CardTitle>
          <CardDescription>Performance metrics for the current month</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Daily Average</p>
              <p className="text-2xl font-bold">156</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Verification Rate</p>
              <p className="text-2xl font-bold">68%</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Broadcast Rate</p>
              <p className="text-2xl font-bold">71%</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Response Time</p>
              <p className="text-2xl font-bold">2.4h</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

